import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  Stack,
  InputAdornment,
  IconButton,
  Tooltip,
  Badge,
  Button,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import TuneIcon from "@mui/icons-material/Tune";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";
import api from "../../../services/api";

import BookingTable from "./BookingTable";
import BookingAccordionMobile from "./BookingAccordionMobile";
import LegacyBookingTable from "./LegacyBookingTable";
import BookingFilters from "./BookingFilters";
import BookingFiltersMobileSheet, {
  DEFAULT_LOCATION,
  DEFAULT_STATUS,
} from "./BookingFiltersMobileSheet";

import useOrdersData from "./hooks/useOrdersData";
import AdminBookingHeader from "./components/AdminBookingHeader";
import LoadingCenter from "./components/LoadingCenter";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog";
import { useAdminNav } from "../../../layouts/AdminNavContext";
import {
  getStoreLocations,
  normalizeLocation,
} from "../../../utils/fulfillment";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const AdminBookingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const snackbar = useSnackbarAlert();
  const { openNav } = useAdminNav();

  // View / filters (committed state)
  const [showLegacy, setShowLegacy] = useState(false);
  const [dateOn, setDateOn] = useState("pickup");
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState(DEFAULT_LOCATION);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS);

  // UI
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // “Updated … ago”
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => (t + 1) % 1000), 15000);
    return () => clearInterval(id);
  }, []);

  const searchRef = useRef(null);
  const { orders, loading, refresh, lastRefreshedAt } = useOrdersData({
    showLegacy,
    dateOn,
    startDate,
    endDate,
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
        return;
      const k = e.key.toLowerCase();
      if (k === "f") searchRef.current?.focus();
      if (k === "r") refresh();
      if (k === "m") openNav?.(); // quick open menu
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [refresh, openNav]);

  // Options
  // const locations = Array.from(
  //   new Set(
  //     orders
  //       .map((o) =>
  //         showLegacy ? o.collectionLocation : normalizeLocation(o.fulfillment)
  //       )
  //       .filter(Boolean)
  //   )
  // );

  const storeLocations = getStoreLocations(); // exact list from AppConfig
  const locations = storeLocations;

  const statuses = Array.from(
    new Set([
      ...orders
        .map((o) => (showLegacy ? o.orderStatus : o.meta?.status))
        .filter(Boolean),
      DEFAULT_STATUS,
    ])
  );

  // In-memory filtering
  const filteredOrders = showLegacy
    ? orders
    : orders.filter((order) => {
        const customer = order.customer || {};
        const fulfillment = order.fulfillment || {};
        const meta = order.meta || {};
        const payment = order.payment || {};

        const search = searchQuery.toLowerCase();
        const matchesSearch =
          customer.firstName?.toLowerCase().includes(search) ||
          customer.lastName?.toLowerCase().includes(search) ||
          customer.email?.toLowerCase().includes(search) ||
          meta.orderId?.toLowerCase().includes(search) ||
          payment.receiptNumber?.toLowerCase().includes(search) ||
          payment.stripeReceiptNumber?.toLowerCase().includes(search);

        // ✅ use normalized location here
        const matchesLocation =
          !locationFilter || normalizeLocation(fulfillment) === locationFilter;

        const targetDate =
          dateOn === "order" ? meta.createdAt : fulfillment.date;
        const d = targetDate
          ? dayjs.tz(targetDate?.$date || targetDate, "America/St_Johns")
          : null;

        const s = startDate
          ? dayjs.tz(startDate, "America/St_Johns").startOf("day")
          : null;
        const e = endDate
          ? dayjs.tz(endDate, "America/St_Johns").endOf("day")
          : null;

        const matchesDateRange =
          (!s || (d && d.isSameOrAfter(s))) &&
          (!e || (d && d.isSameOrBefore(e)));
        const matchesStatus = !statusFilter || meta.status === statusFilter;

        return (
          matchesSearch && matchesLocation && matchesDateRange && matchesStatus
        );
      });

  const clearFilters = () => {
    setSearchQuery("");
    setLocationFilter(DEFAULT_LOCATION);
    setStartDate("");
    setEndDate("");
    setStatusFilter(DEFAULT_STATUS);
    setDateOn("pickup");
  };

  const handleDeleteOrder = async () => {
    if (!selectedOrderId) return;
    try {
      await api.delete(`/orders/${selectedOrderId}`);
      snackbar.success("Order Deleted!");
      setConfirmOpen(false);
      setSelectedOrderId(null);
      refresh();
    } catch (err) {
      const status = err?.response?.status;
      snackbar.error(
        status === 403
          ? "You are not authorized to delete orders."
          : "Failed to delete the order"
      );
      console.error("Delete failed", err);
    }
  };

  const handleSendConfirmation = async (orderId) => {
    try {
      const res = await api.post(
        `/admin/notifications/send-order-confirmation/${orderId}`
      );
      if (res.status === 200 || res.data?.success)
        snackbar.success("Confirmation email sent.");
      else snackbar.error("Failed to send email.");
    } catch (err) {
      console.error("Email send failed", err);
      snackbar.error("Error sending confirmation email.");
    }
  };

  if (loading) return <LoadingCenter text="Loading orders…" />;

  const appliedCount =
    (locationFilter !== DEFAULT_LOCATION ? 1 : 0) +
    (startDate ? 1 : 0) +
    (endDate ? 1 : 0) +
    (statusFilter !== DEFAULT_STATUS ? 1 : 0) +
    (dateOn !== "pickup" ? 1 : 0);

  const hasApplied = appliedCount > 0;

  // build vertical summary lines
  const appliedLines = [];
  if (locationFilter) appliedLines.push(`Location: ${locationFilter}`);
  if (startDate || endDate)
    appliedLines.push(`From ${startDate || "—"} to ${endDate || "—"}`);
  if (statusFilter !== DEFAULT_STATUS)
    appliedLines.push(`Status: ${statusFilter}`);
  if (dateOn !== "pickup")
    appliedLines.push(
      `Date on: ${dateOn === "order" ? "Order date" : "Pickup date"}`
    );

  return (
    <Box sx={{ width: "100%", overflowX: "clip" }}>
      <AdminBookingHeader
        showLegacy={showLegacy}
        onToggleLegacy={setShowLegacy}
        onRefresh={refresh}
        filteredCount={filteredOrders.length}
        totalCount={orders.length}
        lastRefreshedAt={lastRefreshedAt}
        tick={tick}
        onOpenNav={openNav} // NEW: desktop menu button
      />

      {/* MOBILE: compact search with menu & filter icons */}
      {!showLegacy && isMobile && (
        <Box
          sx={{
            px: 2,
            pt: 1,
            pb: 1,
            position: "sticky",
            top: 0,
            zIndex: 1,
            bgcolor: "background.paper",
          }}>
          <TextField
            inputRef={searchRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search name / email / order id"
            size="small"
            fullWidth
            InputProps={{
              sx: { fontSize: 14, height: 40 },
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                  <IconButton
                    size="small"
                    aria-label="Open menu"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={openNav}>
                    <MenuIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      hasApplied ? `Filters (${appliedCount})` : "Filters"
                    }>
                    <span>
                      <IconButton
                        size="small"
                        edge="end"
                        aria-label="Open filters"
                        aria-haspopup="dialog"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setMobileFiltersOpen(true)}>
                        <Badge
                          color="secondary"
                          overlap="circular"
                          badgeContent={appliedCount}
                          invisible={!hasApplied}>
                          <TuneIcon />
                        </Badge>
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          {hasApplied && (
            <Box sx={{ mt: 0.75 }}>
              <Stack spacing={0.25} sx={{ alignItems: "flex-start" }}>
                {appliedLines.map((line, i) => (
                  <Typography key={i} variant="caption" color="text.secondary">
                    {line}
                  </Typography>
                ))}
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{ textTransform: "none", px: 0, minWidth: 0 }}>
                  Clear all
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* DESKTOP filters unchanged */}
      {!showLegacy && !isMobile && (
        <Box sx={{ px: { xs: 2, sm: 3 } }}>
          <BookingFilters
            isCompact
            searchRef={searchRef}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            locationFilter={locationFilter}
            setLocationFilter={setLocationFilter}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            clearFilters={clearFilters}
            locations={locations}
            statuses={statuses}
            dateOn={dateOn}
            setDateOn={setDateOn}
          />
        </Box>
      )}

      {/* Views */}
      {showLegacy ? (
        <LegacyBookingTable orders={orders} />
      ) : isMobile ? (
        <BookingAccordionMobile
          orders={filteredOrders}
          onDelete={(orderId) => {
            setSelectedOrderId(orderId);
            setConfirmOpen(true);
          }}
          onSendConfirmation={handleSendConfirmation}
        />
      ) : (
        <BookingTable
          orders={filteredOrders}
          onDelete={(orderId) => {
            setSelectedOrderId(orderId);
            setConfirmOpen(true);
          }}
          onSendConfirmation={handleSendConfirmation}
        />
      )}

      <DeleteConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteOrder}
      />

      {/* MOBILE FILTER SHEET (draft -> apply) */}
      <BookingFiltersMobileSheet
        open={mobileFiltersOpen}
        onOpen={() => setMobileFiltersOpen(true)}
        onClose={() => setMobileFiltersOpen(false)}
        locationValue={locationFilter}
        dateOnValue={dateOn}
        startDateValue={startDate}
        endDateValue={endDate}
        statusValue={statusFilter}
        onApply={({ location, dateOn, startDate, endDate, status }) => {
          setLocationFilter(location);
          setDateOn(dateOn);
          setStartDate(startDate);
          setEndDate(endDate);
          setStatusFilter(status);
        }}
        locations={locations}
        statuses={statuses}
      />
    </Box>
  );
};

export default AdminBookingPage;
